import React, { useState } from 'react';
import { Card } from '../ui/Card';
import { Button } from '../ui/Button';
import { BarChartIcon, PlusIcon, DownloadIcon, ChevronDownIcon, FilterIcon, CheckIcon, XIcon, CalendarIcon, UsersIcon, MapPinIcon, TrendingUpIcon, TrendingDownIcon, PercentIcon, AlertTriangleIcon, LineChartIcon, PieChartIcon, FileTextIcon, UserIcon } from 'lucide-react';
import { Badge } from '../ui/Badge';
// Mock data for reports
const mockReportTypes = [{
  id: 'trainings',
  name: 'Trainingsoverzicht'
}, {
  id: 'instructors',
  name: 'Instructeursprestaties'
}, {
  id: 'locations',
  name: 'Locatiebezetting'
}, {
  id: 'clients',
  name: 'Klantanalyse'
}, {
  id: 'financial',
  name: 'Financieel Overzicht'
}];
// Mock data for KPIs
const mockKPIs = {
  totalTrainings: 245,
  totalTrainingsTrend: 'up',
  totalTrainingsPercentage: 12,
  averageOccupancy: 86,
  averageOccupancyTrend: 'up',
  averageOccupancyPercentage: 5,
  instructorUtilization: 78,
  instructorUtilizationTrend: 'down',
  instructorUtilizationPercentage: 3,
  customerSatisfaction: 8.7,
  customerSatisfactionTrend: 'up',
  customerSatisfactionPercentage: 0.2
};
// Mock data for training types distribution
const mockTrainingTypeDistribution = [{
  type: 'Sliptraining Basis',
  count: 98,
  percentage: 40
}, {
  type: 'Defensief Rijden',
  count: 72,
  percentage: 29
}, {
  type: 'Code 95 Training',
  count: 45,
  percentage: 18
}, {
  type: 'Sliptraining Gevorderd',
  count: 20,
  percentage: 8
}, {
  type: 'Calamiteitentraining',
  count: 10,
  percentage: 5
}];
// Mock data for instructor performance
const mockInstructorPerformance = [{
  name: 'Erik de Vries',
  trainings: 58,
  rating: 9.2,
  utilization: 92
}, {
  name: 'Lisa Bakker',
  trainings: 42,
  rating: 9.0,
  utilization: 85
}, {
  name: 'Peter Jansen',
  trainings: 39,
  rating: 8.8,
  utilization: 78
}, {
  name: 'Maria Visser',
  trainings: 36,
  rating: 8.5,
  utilization: 72
}, {
  name: 'Thomas Berg',
  trainings: 28,
  rating: 8.9,
  utilization: 65
}];
// Mock data for location occupancy
const mockLocationOccupancy = [{
  name: 'Rotterdam',
  occupancy: 92,
  trend: 'up'
}, {
  name: 'Utrecht',
  occupancy: 87,
  trend: 'up'
}, {
  name: 'Amsterdam',
  occupancy: 74,
  trend: 'down'
}, {
  name: 'Eindhoven',
  occupancy: 68,
  trend: 'up'
}];
// Mock data for recent reports
const mockRecentReports = [{
  id: 1,
  title: 'Kwartaalrapportage Q2 2023',
  type: 'quarterly',
  date: new Date(2023, 6, 15),
  format: 'pdf'
}, {
  id: 2,
  title: 'Instructeursprestaties Juni 2023',
  type: 'instructors',
  date: new Date(2023, 6, 10),
  format: 'xlsx'
}, {
  id: 3,
  title: 'Trainingsoverzicht Mei 2023',
  type: 'trainings',
  date: new Date(2023, 5, 5),
  format: 'pdf'
}, {
  id: 4,
  title: 'Klantanalyse Q1 2023',
  type: 'clients',
  date: new Date(2023, 3, 12),
  format: 'pdf'
}, {
  id: 5,
  title: 'Locatiebezetting Q1 2023',
  type: 'locations',
  date: new Date(2023, 3, 10),
  format: 'xlsx'
}];
// Mock data for illness reports
const mockIllnessReports = [{
  id: 1,
  instructor: 'Peter Jansen',
  startDate: new Date(2023, 6, 20),
  endDate: null,
  status: 'active',
  replacementFound: false
}, {
  id: 2,
  instructor: 'Maria Visser',
  startDate: new Date(2023, 6, 18),
  endDate: new Date(2023, 6, 25),
  status: 'planned',
  replacementFound: true,
  replacement: 'Thomas Berg'
}, {
  id: 3,
  instructor: 'Erik de Vries',
  startDate: new Date(2023, 6, 10),
  endDate: new Date(2023, 6, 15),
  status: 'completed',
  replacementFound: true,
  replacement: 'Lisa Bakker'
}];
export function Reports() {
  const [selectedReportType, setSelectedReportType] = useState<string | null>(null);
  const [dateRange, setDateRange] = useState({
    start: new Date(new Date().getFullYear(), new Date().getMonth() - 1, 1),
    end: new Date()
  });
  const [showFilters, setShowFilters] = useState(false);
  const [activeTab, setActiveTab] = useState<'overview' | 'trainings' | 'instructors' | 'illness'>('overview');
  const [showFeedback, setShowFeedback] = useState(false);
  const [feedbackMessage, setFeedbackMessage] = useState('');
  // Format date
  const formatDate = (date: Date | null) => {
    if (!date) return 'Heden';
    return date.toLocaleDateString('nl-NL', {
      day: 'numeric',
      month: 'long',
      year: 'numeric'
    });
  };
  // Format date for input
  const formatDateForInput = (date: Date) => {
    return date.toISOString().split('T')[0];
  };
  // Generate report
  const generateReport = () => {
    showFeedbackMessage('Rapport wordt gegenereerd en gedownload...');
  };
  // Show feedback message
  const showFeedbackMessage = (message: string) => {
    setFeedbackMessage(message);
    setShowFeedback(true);
    setTimeout(() => {
      setShowFeedback(false);
    }, 3000);
  };
  // Get trend icon
  const getTrendIcon = (trend: string, percentage: number) => {
    if (trend === 'up') {
      return <div className="flex items-center text-green-600" data-id="element-2511">
          <TrendingUpIcon className="h-4 w-4 mr-1" data-id="element-2512" />
          <span data-id="element-2513">+{percentage}%</span>
        </div>;
    } else {
      return <div className="flex items-center text-red-600" data-id="element-2514">
          <TrendingDownIcon className="h-4 w-4 mr-1" data-id="element-2515" />
          <span data-id="element-2516">-{percentage}%</span>
        </div>;
    }
  };
  return <div className="space-y-6" data-id="element-2517">
      <div className="flex justify-between items-center" data-id="element-2518">
        <h1 className="text-2xl font-bold text-gray-900" data-id="element-2519">Rapportages</h1>
        <div className="flex space-x-2" data-id="element-2520">
          <Button variant="outline" size="sm" onClick={() => setShowFilters(!showFilters)} data-id="element-2521">
            <FilterIcon className="h-4 w-4 mr-1" data-id="element-2522" />
            Filters
          </Button>
          <Button variant="primary" size="sm" onClick={generateReport} data-id="element-2523">
            <PlusIcon className="h-4 w-4 mr-1" data-id="element-2524" />
            Genereer Rapport
          </Button>
        </div>
      </div>

      {/* Feedback message */}
      {showFeedback && <div className="p-3 rounded-md bg-blue-100 text-blue-800" data-id="element-2525">
          <div className="flex items-center" data-id="element-2526">
            <DownloadIcon className="h-4 w-4 mr-2" data-id="element-2527" />
            <p data-id="element-2528">{feedbackMessage}</p>
          </div>
        </div>}

      {/* Filters */}
      {showFilters && <Card data-id="element-2529">
          <div className="p-4 space-y-4" data-id="element-2530">
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4" data-id="element-2531">
              {/* Report type filter */}
              <div data-id="element-2532">
                <h3 className="text-sm font-medium text-gray-700 mb-2" data-id="element-2533">
                  Rapport Type
                </h3>
                <select className="w-full p-2 border border-gray-300 rounded-md" value={selectedReportType || ''} onChange={e => setSelectedReportType(e.target.value || null)} data-id="element-2534">
                  <option value="" data-id="element-2535">Alle rapporten</option>
                  {mockReportTypes.map(type => <option key={type.id} value={type.id} data-id="element-2536">
                      {type.name}
                    </option>)}
                </select>
              </div>

              {/* Date range filters */}
              <div data-id="element-2537">
                <h3 className="text-sm font-medium text-gray-700 mb-2" data-id="element-2538">
                  Startdatum
                </h3>
                <input type="date" className="w-full p-2 border border-gray-300 rounded-md" value={formatDateForInput(dateRange.start)} onChange={e => {
              if (e.target.value) {
                setDateRange({
                  ...dateRange,
                  start: new Date(e.target.value)
                });
              }
            }} data-id="element-2539" />
              </div>

              <div data-id="element-2540">
                <h3 className="text-sm font-medium text-gray-700 mb-2" data-id="element-2541">
                  Einddatum
                </h3>
                <input type="date" className="w-full p-2 border border-gray-300 rounded-md" value={formatDateForInput(dateRange.end)} onChange={e => {
              if (e.target.value) {
                setDateRange({
                  ...dateRange,
                  end: new Date(e.target.value)
                });
              }
            }} data-id="element-2542" />
              </div>
            </div>

            <div className="flex justify-end space-x-2 pt-2 border-t border-gray-200" data-id="element-2543">
              <Button variant="outline" size="sm" onClick={() => setShowFilters(false)} data-id="element-2544">
                <XIcon className="h-4 w-4 mr-1" data-id="element-2545" />
                Sluiten
              </Button>
              <Button variant="primary" size="sm" onClick={() => {
            setShowFilters(false);
            showFeedbackMessage('Filters toegepast');
          }} data-id="element-2546">
                <CheckIcon className="h-4 w-4 mr-1" data-id="element-2547" />
                Toepassen
              </Button>
            </div>
          </div>
        </Card>}

      {/* Tabs */}
      <div className="flex space-x-2 border-b border-gray-200 pb-2" data-id="element-2548">
        <Button variant={activeTab === 'overview' ? 'primary' : 'outline'} size="sm" onClick={() => setActiveTab('overview')} data-id="element-2549">
          <BarChartIcon className="h-4 w-4 mr-1" data-id="element-2550" />
          Overzicht
        </Button>
        <Button variant={activeTab === 'trainings' ? 'primary' : 'outline'} size="sm" onClick={() => setActiveTab('trainings')} data-id="element-2551">
          <CalendarIcon className="h-4 w-4 mr-1" data-id="element-2552" />
          Trainingen
        </Button>
        <Button variant={activeTab === 'instructors' ? 'primary' : 'outline'} size="sm" onClick={() => setActiveTab('instructors')} data-id="element-2553">
          <UsersIcon className="h-4 w-4 mr-1" data-id="element-2554" />
          Instructeurs
        </Button>
        <Button variant={activeTab === 'illness' ? 'primary' : 'outline'} size="sm" onClick={() => setActiveTab('illness')} data-id="element-2555">
          <AlertTriangleIcon className="h-4 w-4 mr-1" data-id="element-2556" />
          Ziekmeldingen
        </Button>
      </div>

      {/* Overview Tab */}
      {activeTab === 'overview' && <>
          {/* KPI Widgets */}
          <div className="grid grid-cols-1 md:grid-cols-4 gap-4" data-id="element-2557">
            <Card className="col-span-1" data-id="element-2558">
              <div className="p-4" data-id="element-2559">
                <div className="flex items-center" data-id="element-2560">
                  <div className="p-3 rounded-full bg-blue-100" data-id="element-2561">
                    <CalendarIcon className="h-6 w-6 text-blue-600" data-id="element-2562" />
                  </div>
                  <div className="ml-4" data-id="element-2563">
                    <h3 className="text-sm font-medium text-gray-500" data-id="element-2564">
                      Totaal Trainingen
                    </h3>
                    <div className="flex items-center justify-between" data-id="element-2565">
                      <p className="text-2xl font-semibold text-gray-900" data-id="element-2566">
                        {mockKPIs.totalTrainings}
                      </p>
                      {getTrendIcon(mockKPIs.totalTrainingsTrend, mockKPIs.totalTrainingsPercentage)}
                    </div>
                    <p className="text-xs text-gray-500" data-id="element-2567">Afgelopen 30 dagen</p>
                  </div>
                </div>
              </div>
            </Card>
            <Card className="col-span-1" data-id="element-2568">
              <div className="p-4" data-id="element-2569">
                <div className="flex items-center" data-id="element-2570">
                  <div className="p-3 rounded-full bg-green-100" data-id="element-2571">
                    <PercentIcon className="h-6 w-6 text-green-600" data-id="element-2572" />
                  </div>
                  <div className="ml-4" data-id="element-2573">
                    <h3 className="text-sm font-medium text-gray-500" data-id="element-2574">
                      Gemiddelde Bezetting
                    </h3>
                    <div className="flex items-center justify-between" data-id="element-2575">
                      <p className="text-2xl font-semibold text-gray-900" data-id="element-2576">
                        {mockKPIs.averageOccupancy}%
                      </p>
                      {getTrendIcon(mockKPIs.averageOccupancyTrend, mockKPIs.averageOccupancyPercentage)}
                    </div>
                    <div className="w-full h-2 bg-gray-200 rounded-full mt-1" data-id="element-2577">
                      <div className="h-2 bg-[#B61C1C] rounded-full" style={{
                    width: `${mockKPIs.averageOccupancy}%`
                  }} data-id="element-2578"></div>
                    </div>
                  </div>
                </div>
              </div>
            </Card>
            <Card className="col-span-1" data-id="element-2579">
              <div className="p-4" data-id="element-2580">
                <div className="flex items-center" data-id="element-2581">
                  <div className="p-3 rounded-full bg-purple-100" data-id="element-2582">
                    <UsersIcon className="h-6 w-6 text-purple-600" data-id="element-2583" />
                  </div>
                  <div className="ml-4" data-id="element-2584">
                    <h3 className="text-sm font-medium text-gray-500" data-id="element-2585">
                      Instructeur Bezetting
                    </h3>
                    <div className="flex items-center justify-between" data-id="element-2586">
                      <p className="text-2xl font-semibold text-gray-900" data-id="element-2587">
                        {mockKPIs.instructorUtilization}%
                      </p>
                      {getTrendIcon(mockKPIs.instructorUtilizationTrend, mockKPIs.instructorUtilizationPercentage)}
                    </div>
                    <div className="w-full h-2 bg-gray-200 rounded-full mt-1" data-id="element-2588">
                      <div className="h-2 bg-[#B61C1C] rounded-full" style={{
                    width: `${mockKPIs.instructorUtilization}%`
                  }} data-id="element-2589"></div>
                    </div>
                  </div>
                </div>
              </div>
            </Card>
            <Card className="col-span-1" data-id="element-2590">
              <div className="p-4" data-id="element-2591">
                <div className="flex items-center" data-id="element-2592">
                  <div className="p-3 rounded-full bg-yellow-100" data-id="element-2593">
                    <LineChartIcon className="h-6 w-6 text-yellow-600" data-id="element-2594" />
                  </div>
                  <div className="ml-4" data-id="element-2595">
                    <h3 className="text-sm font-medium text-gray-500" data-id="element-2596">
                      Klanttevredenheid
                    </h3>
                    <div className="flex items-center justify-between" data-id="element-2597">
                      <p className="text-2xl font-semibold text-gray-900" data-id="element-2598">
                        {mockKPIs.customerSatisfaction}
                      </p>
                      {getTrendIcon(mockKPIs.customerSatisfactionTrend, mockKPIs.customerSatisfactionPercentage)}
                    </div>
                    <div className="flex items-center mt-1" data-id="element-2599">
                      {Array.from({
                    length: 5
                  }).map((_, i) => <div key={i} className={`h-2 w-8 ${i < Math.floor(mockKPIs.customerSatisfaction / 2) ? 'bg-[#B61C1C]' : 'bg-gray-200'} ${i > 0 ? 'ml-1' : ''}`} data-id="element-2600"></div>)}
                    </div>
                  </div>
                </div>
              </div>
            </Card>
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-3 gap-6" data-id="element-2601">
            {/* Training Type Distribution */}
            <Card title="Trainingstype Verdeling" className="col-span-1" data-id="element-2602">
              <div className="p-4 space-y-4" data-id="element-2603">
                {mockTrainingTypeDistribution.map(item => <div key={item.type} className="flex items-center justify-between" data-id="element-2604">
                    <div className="flex-1" data-id="element-2605">
                      <div className="flex items-center" data-id="element-2606">
                        <span className="text-sm font-medium text-gray-900" data-id="element-2607">
                          {item.type}
                        </span>
                        <span className="ml-2 text-xs text-gray-500" data-id="element-2608">
                          ({item.count})
                        </span>
                      </div>
                      <div className="w-full h-2 bg-gray-200 rounded-full mt-1" data-id="element-2609">
                        <div className="h-2 bg-[#B61C1C] rounded-full" style={{
                    width: `${item.percentage}%`
                  }} data-id="element-2610"></div>
                      </div>
                    </div>
                    <span className="ml-2 text-sm font-medium text-gray-900" data-id="element-2611">
                      {item.percentage}%
                    </span>
                  </div>)}
                <div className="flex justify-center mt-4" data-id="element-2612">
                  <Button variant="outline" size="sm" data-id="element-2613">
                    <PieChartIcon className="h-4 w-4 mr-1" data-id="element-2614" />
                    Bekijk Volledig Rapport
                  </Button>
                </div>
              </div>
            </Card>

            {/* Top Instructors */}
            <Card title="Top Instructeurs" className="col-span-1" data-id="element-2615">
              <div className="p-4 space-y-4" data-id="element-2616">
                {mockInstructorPerformance.slice(0, 3).map(instructor => <div key={instructor.name} className="flex items-center justify-between" data-id="element-2617">
                    <div className="flex items-center" data-id="element-2618">
                      <div className="h-8 w-8 rounded-full bg-gray-200 flex items-center justify-center" data-id="element-2619">
                        <UserIcon className="h-4 w-4 text-gray-600" data-id="element-2620" />
                      </div>
                      <div className="ml-3" data-id="element-2621">
                        <p className="text-sm font-medium text-gray-900" data-id="element-2622">
                          {instructor.name}
                        </p>
                        <div className="flex items-center text-xs text-gray-500" data-id="element-2623">
                          <span data-id="element-2624">{instructor.trainings} trainingen</span>
                          <span className="mx-1" data-id="element-2625">•</span>
                          <span data-id="element-2626">{instructor.rating} rating</span>
                        </div>
                      </div>
                    </div>
                    <div className="flex items-center" data-id="element-2627">
                      <div className="w-12 h-12 relative" data-id="element-2628">
                        <svg viewBox="0 0 36 36" className="w-12 h-12" data-id="element-2629">
                          <path d="M18 2.0845
                              a 15.9155 15.9155 0 0 1 0 31.831
                              a 15.9155 15.9155 0 0 1 0 -31.831" fill="none" stroke="#eee" strokeWidth="3" data-id="element-2630" />
                          <path d="M18 2.0845
                              a 15.9155 15.9155 0 0 1 0 31.831
                              a 15.9155 15.9155 0 0 1 0 -31.831" fill="none" stroke="#B61C1C" strokeWidth="3" strokeDasharray={`${instructor.utilization}, 100`} data-id="element-2631" />
                        </svg>
                        <div className="absolute top-0 left-0 w-full h-full flex items-center justify-center" data-id="element-2632">
                          <span className="text-xs font-medium" data-id="element-2633">
                            {instructor.utilization}%
                          </span>
                        </div>
                      </div>
                    </div>
                  </div>)}
                <div className="flex justify-center mt-4" data-id="element-2634">
                  <Button variant="outline" size="sm" data-id="element-2635">
                    <UsersIcon className="h-4 w-4 mr-1" data-id="element-2636" />
                    Bekijk Alle Instructeurs
                  </Button>
                </div>
              </div>
            </Card>

            {/* Location Occupancy */}
            <Card title="Locatie Bezetting" className="col-span-1" data-id="element-2637">
              <div className="p-4 space-y-4" data-id="element-2638">
                {mockLocationOccupancy.map(location => <div key={location.name} className="flex items-center justify-between" data-id="element-2639">
                    <div className="flex-1" data-id="element-2640">
                      <div className="flex items-center" data-id="element-2641">
                        <MapPinIcon className="h-4 w-4 text-gray-500 mr-2" data-id="element-2642" />
                        <span className="text-sm font-medium text-gray-900" data-id="element-2643">
                          {location.name}
                        </span>
                      </div>
                      <div className="w-full h-2 bg-gray-200 rounded-full mt-1" data-id="element-2644">
                        <div className={`h-2 rounded-full ${location.occupancy > 90 ? 'bg-green-500' : location.occupancy > 70 ? 'bg-[#B61C1C]' : 'bg-yellow-500'}`} style={{
                    width: `${location.occupancy}%`
                  }} data-id="element-2645"></div>
                      </div>
                    </div>
                    <div className="flex items-center ml-4" data-id="element-2646">
                      <span className="text-sm font-bold text-gray-900 mr-2" data-id="element-2647">
                        {location.occupancy}%
                      </span>
                      {location.trend === 'up' ? <TrendingUpIcon className="h-4 w-4 text-green-500" data-id="element-2648" /> : <TrendingDownIcon className="h-4 w-4 text-red-500" data-id="element-2649" />}
                    </div>
                  </div>)}
                <div className="flex justify-center mt-4" data-id="element-2650">
                  <Button variant="outline" size="sm" data-id="element-2651">
                    <MapPinIcon className="h-4 w-4 mr-1" data-id="element-2652" />
                    Bekijk Locatie Details
                  </Button>
                </div>
              </div>
            </Card>
          </div>

          {/* Recent Reports */}
          <Card title="Recente Rapporten" actions={<Button variant="outline" size="sm" data-id="element-2654">
                <FileTextIcon className="h-4 w-4 mr-1" data-id="element-2655" />
                Bekijk Alle
              </Button>} data-id="element-2653">
            <div className="p-4" data-id="element-2656">
              <div className="overflow-x-auto" data-id="element-2657">
                <table className="min-w-full divide-y divide-gray-200" data-id="element-2658">
                  <thead className="bg-gray-50" data-id="element-2659">
                    <tr data-id="element-2660">
                      <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider" data-id="element-2661">
                        Rapport
                      </th>
                      <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider" data-id="element-2662">
                        Type
                      </th>
                      <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider" data-id="element-2663">
                        Datum
                      </th>
                      <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider" data-id="element-2664">
                        Formaat
                      </th>
                      <th scope="col" className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider" data-id="element-2665">
                        Acties
                      </th>
                    </tr>
                  </thead>
                  <tbody className="bg-white divide-y divide-gray-200" data-id="element-2666">
                    {mockRecentReports.map(report => <tr key={report.id} data-id="element-2667">
                        <td className="px-6 py-4 whitespace-nowrap" data-id="element-2668">
                          <div className="text-sm font-medium text-gray-900" data-id="element-2669">
                            {report.title}
                          </div>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap" data-id="element-2670">
                          <Badge variant={report.type === 'quarterly' ? 'info' : report.type === 'instructors' ? 'success' : report.type === 'trainings' ? 'warning' : 'default'} data-id="element-2671">
                            {report.type === 'quarterly' && 'Kwartaal'}
                            {report.type === 'instructors' && 'Instructeurs'}
                            {report.type === 'trainings' && 'Trainingen'}
                            {report.type === 'clients' && 'Klanten'}
                            {report.type === 'locations' && 'Locaties'}
                          </Badge>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap" data-id="element-2672">
                          <div className="text-sm text-gray-500" data-id="element-2673">
                            {formatDate(report.date)}
                          </div>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap" data-id="element-2674">
                          <span className="text-sm text-gray-500 uppercase" data-id="element-2675">
                            {report.format}
                          </span>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium" data-id="element-2676">
                          <Button variant="outline" size="sm" data-id="element-2677">
                            <DownloadIcon className="h-4 w-4 mr-1" data-id="element-2678" />
                            Download
                          </Button>
                        </td>
                      </tr>)}
                  </tbody>
                </table>
              </div>
            </div>
          </Card>
        </>}

      {/* Trainings Tab */}
      {activeTab === 'trainings' && <Card title="Trainingsoverzicht" data-id="element-2679">
          <div className="p-4" data-id="element-2680">
            <div className="flex justify-center items-center py-12" data-id="element-2681">
              <div className="text-center" data-id="element-2682">
                <BarChartIcon className="h-16 w-16 text-gray-300 mx-auto mb-4" data-id="element-2683" />
                <h3 className="text-lg font-medium text-gray-900 mb-2" data-id="element-2684">
                  Trainingsanalyse
                </h3>
                <p className="text-gray-500 max-w-md mb-6" data-id="element-2685">
                  Hier worden gedetailleerde analyses en grafieken getoond over
                  trainingen, bezettingsgraad, en andere relevante statistieken.
                </p>
                <div className="flex justify-center space-x-2" data-id="element-2686">
                  <Button variant="outline" data-id="element-2687">
                    <FilterIcon className="h-4 w-4 mr-1" data-id="element-2688" />
                    Filters Aanpassen
                  </Button>
                  <Button variant="primary" data-id="element-2689">
                    <DownloadIcon className="h-4 w-4 mr-1" data-id="element-2690" />
                    Rapport Downloaden
                  </Button>
                </div>
              </div>
            </div>
          </div>
        </Card>}

      {/* Instructors Tab */}
      {activeTab === 'instructors' && <Card title="Instructeursprestaties" data-id="element-2691">
          <div className="p-4" data-id="element-2692">
            <div className="overflow-x-auto" data-id="element-2693">
              <table className="min-w-full divide-y divide-gray-200" data-id="element-2694">
                <thead className="bg-gray-50" data-id="element-2695">
                  <tr data-id="element-2696">
                    <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider" data-id="element-2697">
                      Instructeur
                    </th>
                    <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider" data-id="element-2698">
                      Trainingen
                    </th>
                    <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider" data-id="element-2699">
                      Beoordeling
                    </th>
                    <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider" data-id="element-2700">
                      Bezetting
                    </th>
                    <th scope="col" className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider" data-id="element-2701">
                      Details
                    </th>
                  </tr>
                </thead>
                <tbody className="bg-white divide-y divide-gray-200" data-id="element-2702">
                  {mockInstructorPerformance.map(instructor => <tr key={instructor.name} data-id="element-2703">
                      <td className="px-6 py-4 whitespace-nowrap" data-id="element-2704">
                        <div className="flex items-center" data-id="element-2705">
                          <div className="h-8 w-8 rounded-full bg-gray-200 flex items-center justify-center" data-id="element-2706">
                            <UserIcon className="h-4 w-4 text-gray-600" data-id="element-2707" />
                          </div>
                          <div className="ml-3" data-id="element-2708">
                            <div className="text-sm font-medium text-gray-900" data-id="element-2709">
                              {instructor.name}
                            </div>
                          </div>
                        </div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap" data-id="element-2710">
                        <div className="text-sm text-gray-900" data-id="element-2711">
                          {instructor.trainings}
                        </div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap" data-id="element-2712">
                        <div className="flex items-center" data-id="element-2713">
                          <div className="text-sm font-medium text-gray-900 mr-2" data-id="element-2714">
                            {instructor.rating}
                          </div>
                          <div className="flex" data-id="element-2715">
                            {Array.from({
                        length: 5
                      }).map((_, i) => <div key={i} className={`h-1.5 w-5 ${i < Math.floor(instructor.rating) ? 'bg-[#B61C1C]' : 'bg-gray-200'} ${i > 0 ? 'ml-0.5' : ''}`} data-id="element-2716"></div>)}
                          </div>
                        </div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap" data-id="element-2717">
                        <div className="flex items-center" data-id="element-2718">
                          <div className="text-sm font-medium text-gray-900 mr-2" data-id="element-2719">
                            {instructor.utilization}%
                          </div>
                          <div className="w-24 h-2 bg-gray-200 rounded-full" data-id="element-2720">
                            <div className={`h-2 rounded-full ${instructor.utilization > 90 ? 'bg-green-500' : instructor.utilization > 70 ? 'bg-[#B61C1C]' : 'bg-yellow-500'}`} style={{
                        width: `${instructor.utilization}%`
                      }} data-id="element-2721"></div>
                          </div>
                        </div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium" data-id="element-2722">
                        <Button variant="outline" size="sm" data-id="element-2723">
                          <ChevronDownIcon className="h-4 w-4" data-id="element-2724" />
                        </Button>
                      </td>
                    </tr>)}
                </tbody>
              </table>
            </div>
          </div>
        </Card>}

      {/* Illness Tab */}
      {activeTab === 'illness' && <Card title="Ziekmeldingen" data-id="element-2725">
          <div className="p-4" data-id="element-2726">
            <div className="overflow-x-auto" data-id="element-2727">
              <table className="min-w-full divide-y divide-gray-200" data-id="element-2728">
                <thead className="bg-gray-50" data-id="element-2729">
                  <tr data-id="element-2730">
                    <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider" data-id="element-2731">
                      Instructeur
                    </th>
                    <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider" data-id="element-2732">
                      Startdatum
                    </th>
                    <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider" data-id="element-2733">
                      Einddatum
                    </th>
                    <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider" data-id="element-2734">
                      Status
                    </th>
                    <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider" data-id="element-2735">
                      Vervanging
                    </th>
                    <th scope="col" className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider" data-id="element-2736">
                      Acties
                    </th>
                  </tr>
                </thead>
                <tbody className="bg-white divide-y divide-gray-200" data-id="element-2737">
                  {mockIllnessReports.map(report => <tr key={report.id} data-id="element-2738">
                      <td className="px-6 py-4 whitespace-nowrap" data-id="element-2739">
                        <div className="flex items-center" data-id="element-2740">
                          <div className="text-sm font-medium text-gray-900" data-id="element-2741">
                            {report.instructor}
                          </div>
                        </div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap" data-id="element-2742">
                        <div className="text-sm text-gray-900" data-id="element-2743">
                          {formatDate(report.startDate)}
                        </div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap" data-id="element-2744">
                        <div className="text-sm text-gray-900" data-id="element-2745">
                          {formatDate(report.endDate)}
                        </div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap" data-id="element-2746">
                        <Badge variant={report.status === 'active' ? 'danger' : report.status === 'planned' ? 'warning' : 'success'} data-id="element-2747">
                          {report.status === 'active' && 'Actief'}
                          {report.status === 'planned' && 'Gepland'}
                          {report.status === 'completed' && 'Afgerond'}
                        </Badge>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap" data-id="element-2748">
                        {report.replacementFound ? <div className="flex items-center" data-id="element-2749">
                            <CheckIcon className="h-4 w-4 text-green-500 mr-1" data-id="element-2750" />
                            <span className="text-sm text-gray-900" data-id="element-2751">
                              {report.replacement}
                            </span>
                          </div> : <div className="flex items-center" data-id="element-2752">
                            <XIcon className="h-4 w-4 text-red-500 mr-1" data-id="element-2753" />
                            <span className="text-sm text-gray-500" data-id="element-2754">
                              Geen vervanging
                            </span>
                          </div>}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium" data-id="element-2755">
                        <Button variant="outline" size="sm" data-id="element-2756">
                          Details
                        </Button>
                      </td>
                    </tr>)}
                </tbody>
              </table>
            </div>
          </div>
        </Card>}
    </div>;
}